import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../context/API';
import { User } from '../components/RoleManagement/types';

// API Response Interfaces
interface RoleUserDataItem {
  role: string;
  id: string;
  name: string;
  username: string;
  address: string;
  email: string;
  password: string;
  phone: string;
  staff: any[];
}

interface RoleUserData {
  message: string;
  status: number;
  data: RoleUserDataItem[];
  success: boolean;
}

// API endpoints
const ROLE_MANAGEMENT_ENDPOINTS = {
  GET_ALL_USERS: "/manager/all",
  DELETE_USER: "/manager/{id}",
  UPDATE_ROLE: "/users/{userId}/role",
  GET_ROLE_STATS: "/users/roles/stats",
};

// Helper function to get endpoint with ID replacement
const getEndpoint = (template: string, id: string): string => 
  template.replace("{id}", id).replace("{userId}", id);

// Async thunks
export const fetchUsers = createAsyncThunk<User[], void, { rejectValue: string }>(
  'roleManagement/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<RoleUserData>(ROLE_MANAGEMENT_ENDPOINTS.GET_ALL_USERS);
      console.log("response.data", response);
      // Map API response data to User type (they match, so we can return directly)
      return response?.data?.data as User[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch users'
      );
    }
  }
);

export const deleteUser = createAsyncThunk<string, string, { rejectValue: string }>(
  'roleManagement/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      const endpoint = getEndpoint(ROLE_MANAGEMENT_ENDPOINTS.DELETE_USER, userId);
      const response = await API.delete(endpoint, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      
      if (response.data.status === 200) {
        return userId;
      } else {
        return rejectWithValue(
          response.data.message || 'Failed to delete user'
        );
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete user'
      );
    }
  }
);

interface UpdateRolePayload {
  userId: string;
  newRole: string;
}

export const updateUserRole = createAsyncThunk<UpdateRolePayload, UpdateRolePayload, { rejectValue: string }>(
  'roleManagement/updateUserRole',
  async (payload, { rejectWithValue }) => {
    try {
      const endpoint = getEndpoint(ROLE_MANAGEMENT_ENDPOINTS.UPDATE_ROLE, payload.userId);
      const response = await API.put(endpoint, {
        role: payload.newRole,
      }, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      
      if (response.data.status === 200 || response.data.success) {
        return payload;
      } else {
        return rejectWithValue(
          response.data.message || 'Failed to update user role'
        );
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update user role'
      );
    }
  }
);

export const fetchRoleStats = createAsyncThunk<RoleStats, void, { rejectValue: string }>(
  'roleManagement/fetchRoleStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get<RoleStatsResponse>(ROLE_MANAGEMENT_ENDPOINTS.GET_ROLE_STATS);
      
      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        return rejectWithValue('Invalid stats data format');
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch role statistics'
      );
    }
  }
);

// Stats response interface
export interface RoleStats {
  totalUsers: number;
  activeUsers: number;
  usersWithRoles: number;
  usersWithoutRoles: number;
}

interface RoleStatsResponse {
  message?: string;
  status?: number;
  success?: boolean;
  data: RoleStats;
}

interface RoleManagementState {
  RoleUsersData: User[];
  stats: RoleStats | null;
  loading: boolean;
  statsLoading: boolean;
  error: string | null;
}

const initialState: RoleManagementState = {
  RoleUsersData: [],
  stats: null,
  loading: false,
  statsLoading: false,
  error: null,
};


const roleManagementSlice = createSlice({
  name: 'roleManagement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.RoleUsersData = action.payload as User[];
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        // Remove deleted user from state
        state.RoleUsersData = state.RoleUsersData.filter(
          (user) => user.id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update user role
      .addCase(updateUserRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loading = false;
        // Update user role in state
        const { userId, newRole } = action.payload;
        const userIndex = state.RoleUsersData.findIndex((u) => u.id === userId);
        if (userIndex !== -1) {
          state.RoleUsersData[userIndex] = {
            ...state.RoleUsersData[userIndex],
            role: newRole,
          };
        }
        state.error = null;
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch role stats
      .addCase(fetchRoleStats.pending, (state) => {
        state.statsLoading = true;
        state.error = null;
      })
      .addCase(fetchRoleStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(fetchRoleStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default roleManagementSlice.reducer;

